import { Controller } from "@hotwired/stimulus";

// Gives the bottom-right corner back to Intercom when it has something unread.
//
// Kit replaces Intercom as the support entry point, so kit_chat.scss hides
// Intercom's launcher (they overlap almost exactly). That left a hole: once
// ContactSupportTool hands a user off to Intercom, the launcher — and the unread
// badge on it — is the only durable way back into that conversation. Verified in
// a browser: after the handoff and a reload, the corner is identical to baseline
// (Kit's toggle, no launcher, no trace of the conversation), so a reply landing
// hours later is invisible in the app and only reaches them by Intercom's email.
//
// `hide_default_launcher` cannot help here and never could: intercom-rails
// injects its boot snippet after our script and its bare
// `window.intercomSettings = {...}` wipes the flag (measured: the key reads back
// `undefined`, and Intercom's own settings object never contains it). CSS is the
// only lever, so this controller drives CSS — it puts `intercom-has-unread` on
// <body>, which releases the gate in kit_chat.scss and mobile.css.scss.
//
// The result is exactly one bubble in the corner at all times, always the one
// with news: unread > 0 hands the corner to Intercom (with its native badge) and
// steps Kit's toggle aside; reading the message drops the count to 0 and Kit
// takes it back. Never both — stacked launchers is the bug #1104/#1296 kept
// reintroducing.
//
// Lives on <body> rather than on the Kit panel because it must also work for
// tenants without Kit: mobile.css.scss hides the launcher at phone width for
// everyone, so without this an unread reply is unreachable on a phone.

const BODY_CLASS = "intercom-has-unread";
const RETRY_MS = 500;
const MAX_ATTEMPTS = 40; // ~20s — the widget boots async, well after this connects

// Module scope, not instance state: Turbo Drive swaps <body> on every visit,
// which disconnects the controller and drops the class. Intercom's callback only
// fires on CHANGE, so a fresh <body> would come back unmarked and strand the user
// again on the very next navigation. Remember the count and re-apply on connect.
let lastKnownCount = 0;
let registered = false;

export default class extends Controller {
  connect() {
    this.applyCount(lastKnownCount);
    this.attempts = 0;
    this.register();
  }

  disconnect() {
    clearTimeout(this.retryTimer);
  }

  register() {
    if (registered) return;

    // Intercom is absent for users matched by `exclude_if` and outside
    // production, and boots late when it is present — so a miss here is normal;
    // retry rather than give up on the first attempt.
    if (typeof window.Intercom !== "function") {
      if (this.attempts++ >= MAX_ATTEMPTS) return;
      this.retryTimer = setTimeout(() => this.register(), RETRY_MS);
      return;
    }

    // Safe to register against the pre-boot stub: it queues the call and the
    // widget replays it, then fires immediately with the current unread count —
    // which is what covers the user returning hours later to a waiting reply.
    registered = true;
    window.Intercom("onUnreadCountChange", (count) => {
      lastKnownCount = count || 0;
      this.applyCount(lastKnownCount);
    });
  }

  // Always targets the live document.body, never this.element: the callback
  // outlives the <body> it was registered from.
  applyCount(count) {
    document.body.classList.toggle(BODY_CLASS, count > 0);
  }
}
